import { t } from 'i18next';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { RECOGNITION_KEYS } from '../../../services/Recognition/hooks/useQuery';
import { AppContext } from '../../../context/AppContextProvider';
import { useSyncContext } from '../../../context/SyncJobContext';

import { Add, FilterAltOff, LockOpen, Sync } from '@mui/icons-material';
import { Box } from '@mui/material';

import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import ImageModalComponent from '../../../components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../../components/ImageSimpleColumnComponent';
import TableComponent, { IColumnProps } from '../../../components/TableComponent';

import RecognitionNameCell from './_components/cells/RecognitionNameCell';
import FaceSyncRunner from './_components/modals/FaceSyncRunner';
import RecognitionFilterModal from './_components/modals/RecognitionFilterModal';
import RecognitionsEditModal from './_components/modals/RecognitionsEditModal';
import SyncFacesModal from './_components/modals/SyncFacesModal';
import TemporaryReleaseModal from './_components/modals/TemporaryReleaseModal';

import { useDebounce } from '../../../services/Recognition/hooks/useDebounce';
import { useLoadFirebaseImageMutation } from '../../../services/Recognition/hooks/useMutation';
import {
  useGetAllRecognitionsByLicenseCountQuery,
  useGetAllRecognitionsByLicenseQuery,
} from '../../../services/Recognition/hooks/useQuery';

import {
  IRecognitionFilterParams,
  IRecognitionSimpleProps,
  IRecognitionTypes,
} from '../../../services/Recognition/types';
import { IPaginationParams } from '../../../services/types';
import { formatCnpj, formatCpf } from '../../../utils/formatters';
import { getRecognitionTypeLabel } from './utils';
import type { IFaceSyncAsyncCreateResponseProps } from 'services/Jobs/types';
import { useDeleteGeneralRecognitionAsyncJob } from 'services/Recognition/General/hooks/useMutation';
import { useDeleteResponsibleRecognitionAsyncJob } from 'services/Recognition/Responsible/hooks/useMutation';
import { useDeleteStudentRecognitionAsyncJob } from 'services/Recognition/Student/hooks/useMutation';
import { useDeleteCustomerRecognitionAsyncJob } from 'services/Recognition/Customer/hooks/useMutation';
import { useDeleteRecognitionAsync } from 'services/Recognition/hooks/useMutation';
import { useFaceSyncLegacyFlag } from 'services/Configs/useFaceSyncLegacyFlag';
import { useSetStudentTemporaryReleaseAsync } from 'services/Recognition/Student/hooks/useMutation';

const INITIAL_FILTER: IRecognitionFilterParams = {
  search: '',
  discriminator: undefined,
  isActive: undefined,
  temporary: undefined,
};

const INITIAL_PAGINATION: IPaginationParams = {
  pageSize: 10,
  pageNumber: 1,
};

const MemoizedTable = memo(TableComponent);

const RecognitionsPage = () => {
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState<IPaginationParams>(INITIAL_PAGINATION);
  const [filter, setFilter] = useState<IRecognitionFilterParams>(INITIAL_FILTER);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [editModal, setEditModal] = useState<{ isOpen: boolean; item?: IRecognitionSimpleProps }>({ isOpen: false });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item?: IRecognitionSimpleProps }>({ isOpen: false });
  const [imageModal, setImageModal] = useState({ isOpen: false, url: '' });
  const [temporaryReleaseModal, setTemporaryReleaseModal] = useState<{ isOpen: boolean; item?: IRecognitionSimpleProps }>({ isOpen: false });

  const [isShowSyncFacesModal, setIsShowSyncFacesModal] = useState(false);
  const [isVisibleRecognitionFilterModal, setIsVisibleRecognitionFilterModal] = useState(false);

  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const syncCtx = useSyncContext();

  const { mutateAsync: deleteGeneralJob, isLoading: isDeletingGeneral } = useDeleteGeneralRecognitionAsyncJob();
  const { mutateAsync: deleteResponsibleJob, isLoading: isDeletingResponsible } = useDeleteResponsibleRecognitionAsyncJob();
  const { mutateAsync: deleteStudentJob, isLoading: isDeletingStudent } = useDeleteStudentRecognitionAsyncJob();
  const { mutateAsync: deleteCustomerJob, isLoading: isDeletingCustomer } = useDeleteCustomerRecognitionAsyncJob();
  const { mutateAsync: deleteRecognitionLegacy } = useDeleteRecognitionAsync();
  const useLegacy = useFaceSyncLegacyFlag();

  const isEnqueuingDeletion = isDeletingGeneral || isDeletingResponsible || isDeletingStudent || isDeletingCustomer;

  const {
    data: recognitions,
    isLoading: isLoadingRecognitions,
  } = useGetAllRecognitionsByLicenseQuery(pagination, filter);

  const {
    data: recognitionsCount,
    isLoading: isLoadingRecognitionsCount,
  } = useGetAllRecognitionsByLicenseCountQuery(filter);

  const { mutate: loadImage } = useLoadFirebaseImageMutation();
  const { mutateAsync: setTemporaryRelease, isLoading: isSettingTemporaryRelease } = useSetStudentTemporaryReleaseAsync();

  useEffect(() => {
    setFilter((prev) => {
      if (prev.search === debouncedSearchTerm) return prev;
      return { ...prev, search: debouncedSearchTerm };
    });
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [debouncedSearchTerm]);

  const refetchRecognitionsList = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries([RECOGNITION_KEYS.ALL, RECOGNITION_KEYS.LIST]),
      queryClient.invalidateQueries([RECOGNITION_KEYS.ALL, RECOGNITION_KEYS.COUNT]),
    ]);
  }, [queryClient]);

  const handleFilterChange = useCallback((val: string) => {
    setSearchTerm(val);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilter(INITIAL_FILTER); 
    setSearchTerm(''); 
    setPagination(prev => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleOpenEditionModal = useCallback((id?: number) => {
    if (id && recognitions) {
      const item = recognitions.find((i) => i.id === id);
      setEditModal({ isOpen: true, item });
    } else {
      setEditModal({ isOpen: true, item: undefined }); 
    }
  }, [recognitions]);

  const handleCloseEditionModal = useCallback(() => {
    setEditModal({ isOpen: false, item: undefined });
  }, []);

  const handleSaveForm = useCallback(async () => {
    handleCloseEditionModal();
    await queryClient.invalidateQueries([RECOGNITION_KEYS.ALL]);
  }, [handleCloseEditionModal, queryClient]);

  const handleOpenDeletionModal = useCallback((id: number) => {
    if (recognitions) {
      const item = recognitions.find((i) => i.id === id);
      setDeleteModal({ isOpen: true, item });
    }
  }, [recognitions]);

  const handleCloseDeletionModal = useCallback(() => {
    setDeleteModal({ isOpen: false, item: undefined });
  }, []);

  const handleConfirmDeletion = useCallback(async () => {
    if (!deleteModal.item?.id) return;

    const id = deleteModal.item.id;
    const name = deleteModal.item.name;
    const type = deleteModal.item.discriminator as IRecognitionTypes;

    handleCloseDeletionModal();

    if (useLegacy) {
      setIsShowLoadingComponent(true);
      try {
        await deleteRecognitionLegacy({ id, type });
        showSnackbar('Cadastro excluído com sucesso!', 'success');
        await queryClient.invalidateQueries([RECOGNITION_KEYS.ALL]);
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || t('common.errorDeleting');
        showSnackbar(message, 'error');
      } finally {
        setIsShowLoadingComponent(false);
      }
      return;
    }

    syncCtx.startPreparing(name, 'delete');

    try {
      const deleteJobByType: Record<IRecognitionTypes, (id: number) => Promise<IFaceSyncAsyncCreateResponseProps>> = {
        [IRecognitionTypes.STUDENT]: deleteStudentJob,
        [IRecognitionTypes.RESPONSIBLE]: deleteResponsibleJob,
        [IRecognitionTypes.GENERAL]: deleteGeneralJob,
        [IRecognitionTypes.CUSTOMER]: deleteCustomerJob,
      };

      const job = await deleteJobByType[type](id);

      syncCtx.setSyncJob(job.jobId, job.recognitionId, name, 0, job.message ?? 'Enfileirado para exclusão...', 'delete');
      syncCtx.hideToast();
      await queryClient.invalidateQueries([RECOGNITION_KEYS.ALL]);
    } catch (error: any) {
      syncCtx.stopPreparing();
      const message = error?.response?.data?.message || error?.message || t('common.errorDeleting');
      showSnackbar(message, 'error');
    }
  }, [deleteModal.item, useLegacy, deleteRecognitionLegacy, deleteGeneralJob, deleteResponsibleJob, deleteStudentJob, deleteCustomerJob, syncCtx, handleCloseDeletionModal, queryClient, showSnackbar, setIsShowLoadingComponent]);

  const handleOpenImageModal = useCallback((url: string) => {
    if (!url) return;
    loadImage(url, {
      onSuccess: (tempUrl: string | null | undefined) => {
        if (!tempUrl) return;
        setImageModal({ isOpen: true, url: tempUrl });
      },
      onError: (err: unknown) => {
        console.error('Erro ao carregar imagem', err);
      },
    });
  }, [loadImage]);

  const handleCloseImageModal = useCallback(() => {
    setImageModal({ isOpen: false, url: '' });
  }, []);

  const handleOpenFilter = useCallback(() => {
    setIsVisibleRecognitionFilterModal(true);
  }, []);

  const handleOpenTemporaryReleaseModal = useCallback((id: number) => {
    if (recognitions) {
      const item = recognitions.find((i) => i.id === id);
      setTemporaryReleaseModal({ isOpen: true, item });
    }
  }, [recognitions]);

  const handleCloseTemporaryReleaseModal = useCallback(() => {
    setTemporaryReleaseModal({ isOpen: false, item: undefined });
  }, []);

  const handleConfirmTemporaryRelease = useCallback(async (minutes: number) => {
    const id = temporaryReleaseModal.item?.id;
    if (!id) return;

    try {
      await setTemporaryRelease({ id, minutes });
      showSnackbar('Liberação temporária concedida com sucesso!', 'success');
      handleCloseTemporaryReleaseModal();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || t('common.errorSaving');
      showSnackbar(message, 'error');
    }
  }, [temporaryReleaseModal.item, setTemporaryRelease, showSnackbar, handleCloseTemporaryReleaseModal, t]);

  const handleToggleDeleteDialog = useCallback((val: boolean | ((prev: boolean) => boolean)) => {
    setDeleteModal((prev) => ({
      ...prev,
      isOpen: typeof val === 'function' ? val(prev.isOpen) : val,
    }));
  }, []);

  const columns: IColumnProps[] = useMemo(() => [
    {
      id: 'discriminator',
      label: t('recognitionPage.tableHeaders.type'),
      width: 0,
      align: 'center',
      render: (val: string) => <>{getRecognitionTypeLabel(val as IRecognitionTypes)}</>,
    },
    {
      id: 'name',
      label: t('recognitionPage.tableHeaders.name'),
      width: '22%',
      render: (_val: string, _index: number, row: IRecognitionSimpleProps) => <RecognitionNameCell row={row} />,
    },
    {
      id: 'cpf',
      label: t('recognitionPage.tableHeaders.CPF'),
      render: (val: string) => <>{val ? formatCpf(val) : t('common.notAvailable')}</>,
    },
    {
      id: 'cnpj',
      label: t('recognitionPage.tableHeaders.CNPJ'),
      render: (val: string) => <>{val ? formatCnpj(val) : t('common.notAvailable')}</>,
    },
    {
      id: 'image',
      label: t('recognitionPage.tableHeaders.image'),
      width: 0,
      align: 'center',
      render: (url: string) => (
        <ImageSimpleColumnComponent url={url} onClick={() => handleOpenImageModal(url)} />
      ),
    },
    {
      id: 'id',
      label: t('recognitionPage.tableHeaders.actions'),
      align: 'center',
      width: 0,
      render: (idVal: string, _index: number, row: IRecognitionSimpleProps) => {
        const numericId = Number(idVal);
        const isBlockedStudent = row.discriminator === IRecognitionTypes.STUDENT;
        return (
          <ButtonActionsComponent
            key={numericId}
            isWithDefaultOptionsList
            handleDelete={() => handleOpenDeletionModal(numericId)}
            handleEdit={() => handleOpenEditionModal(numericId)}
            actionOptions={isBlockedStudent ? [
              {
                label: 'Liberação temporária',
                icon: <LockOpen fontSize="small" />,
                onClick: () => handleOpenTemporaryReleaseModal(numericId),
              },
            ] : []}
          />
        );
      },
    },
  ], [handleOpenImageModal, handleOpenDeletionModal, handleOpenEditionModal, handleOpenTemporaryReleaseModal, t]);

  const tableTitleButtons = useMemo(() => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        alignItems: { xs: 'flex-end', sm: 'center' }, 
        gap: 1.5,
      }}
    >
      <ButtonComponent 
        size="small"
        onClick={() => handleOpenEditionModal()} 
        startIcon={<Add />}
      >
        {t('recognitionPage.newFace')}
      </ButtonComponent>

      <ButtonComponent 
        color="secondary" 
        size="small"
        onClick={() => setIsShowSyncFacesModal(true)} 
        startIcon={<Sync />}
      >
        {t('recognitionPage.synchronization')}
      </ButtonComponent>

      <ButtonComponent 
        onClick={handleClearFilters} 
        size="small" 
        color="warning" 
        startIcon={<FilterAltOff />}
      >
        {t('recognitionPage.cleanFilters')}
      </ButtonComponent>
    </Box>
  ), [handleClearFilters, handleOpenEditionModal, t]);

  return (
    <>
      <MemoizedTable
        title={t('recognitionPage.title')}
        columns={columns}
        rows={recognitions ?? []}
        sortByName={'name'}
        onOpenFilter={handleOpenFilter} 
        totalCount={recognitionsCount}
        onChangeMainFilter={handleFilterChange}
        onSubmitMainFilter={refetchRecognitionsList}
        isLoading={isLoadingRecognitions || isLoadingRecognitionsCount}
        currentPageNumber={pagination.pageNumber}
        onChangePageOrRowsPerPage={setPagination} 
        searchValue={searchTerm}
        additionalTitleComponent={tableTitleButtons}
      />

      <ConfirmDeleteModalComponent
        isLoading={isEnqueuingDeletion}
        isVisibleDeleteDialog={deleteModal.isOpen}
        setIsVisibleDeleteDialog={handleToggleDeleteDialog}
        title={t('recognitionPage.deleteFace')}
        message={t('recognitionPage.areYouSureWantToDeleteFace', {
          name: deleteModal.item?.name,
        })}
        handleCancelDeletion={handleCloseDeletionModal}
        handleConfirmDeletion={handleConfirmDeletion}
      />

      <ImageModalComponent
        isOpen={imageModal.isOpen}
        handleClose={handleCloseImageModal}
        imageUrl={imageModal.url}
        title={t('recognitionPage.recognitionImage')}
      />

      <RecognitionsEditModal
        isOpen={editModal.isOpen}
        handleClose={handleCloseEditionModal}
        selectedRecognition={editModal.item}
        handleSaveForm={handleSaveForm}
      />

      <SyncFacesModal
        isOpen={isShowSyncFacesModal}
        setIsOpen={setIsShowSyncFacesModal}
        handleCreateRecognition={refetchRecognitionsList}
      />

      <RecognitionFilterModal
        currentFilter={filter}
        onFilter={(newFilterVals) => setFilter(newFilterVals as IRecognitionFilterParams)}
        isOpen={isVisibleRecognitionFilterModal}
        onClose={() => setIsVisibleRecognitionFilterModal(false)}
      />

      <TemporaryReleaseModal
        isOpen={temporaryReleaseModal.isOpen}
        studentName={temporaryReleaseModal.item?.name}
        isLoading={isSettingTemporaryRelease}
        onClose={handleCloseTemporaryReleaseModal}
        onConfirm={handleConfirmTemporaryRelease}
      />

      <FaceSyncRunner />
    </>
  );
};

export default RecognitionsPage;